import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ZoomIn, ZoomOut, Maximize2, Minimize2, RotateCw, RotateCcw, Download, Share2, Trash2, Type, Square, Circle, ArrowRight, CheckCircle, Image, FileText, RotateCw as RotateCwIcon, FileText as FileTextIcon, Square as SquareIcon, Circle as CircleIcon, CheckCircle as CheckCircleIcon } from 'lucide-react';

interface ImageAnnotation {
  id: string;
  type: 'text' | 'arrow' | 'highlight' | 'circle' | 'rectangle';
  x: number;
  y: number;
  width?: number;
  height?: number;
  text?: string;
  color: string;
  createdAt: Date;
  author: string;
}

interface ImageEnhancementProps {
  imageUrl: string;
  imageAlt?: string;
  onAnnotationAdd?: (annotation: ImageAnnotation) => void;
  onAnnotationUpdate?: (annotation: ImageAnnotation) => void;
  onAnnotationDelete?: (annotationId: string) => void;
  onImageDownload?: (imageUrl: string) => void;
  onImageShare?: (imageUrl: string) => void;
}

const ImageEnhancement: React.FC<ImageEnhancementProps> = ({
  imageUrl,
  imageAlt = 'Enhanced image',
  onAnnotationAdd,
  onAnnotationUpdate,
  onAnnotationDelete,
  onImageDownload,
  onImageShare
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [annotations, setAnnotations] = useState<ImageAnnotation[]>([]);
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [annotationType, setAnnotationType] = useState<ImageAnnotation['type']>('text');
  const [annotationColor, setAnnotationColor] = useState('#ff0000');
  const [selectedAnnotation, setSelectedAnnotation] = useState<ImageAnnotation | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showControls, setShowControls] = useState(true);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const annotationCanvasRef = useRef<HTMLCanvasElement>(null);

  const colors = [
    '#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff',
    '#ff8000', '#8000ff', '#0080ff', '#ff0080', '#80ff00', '#0080ff'
  ];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isFullscreen]);

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleResetZoom = () => {
    setZoom(1);
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleRotateCounterClockwise = () => {
    setRotation(prev => (prev - 90 + 360) % 360);
  };

  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isAnnotating || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setDragStart({ x, y });
    setIsDragging(true);

    if (annotationType === 'text') {
      const text = prompt('Enter annotation text:');
      if (text) {
        const annotation: ImageAnnotation = {
          id: Date.now().toString(),
          type: 'text',
          x,
          y,
          text,
          color: annotationColor,
          createdAt: new Date(),
          author: 'current-user'
        };
        setAnnotations(prev => [...prev, annotation]);
        onAnnotationAdd?.(annotation);
      }
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging || !imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (annotationType !== 'text') {
      const annotation: ImageAnnotation = {
        id: Date.now().toString(),
        type: annotationType,
        x: Math.min(dragStart.x, x),
        y: Math.min(dragStart.y, y),
        width: Math.abs(x - dragStart.x),
        height: Math.abs(y - dragStart.y),
        color: annotationColor,
        createdAt: new Date(),
        author: 'current-user'
      };
      setAnnotations(prev => [...prev, annotation]);
      onAnnotationAdd?.(annotation);
    }

    setIsDragging(false);
  };

  const handleAnnotationClick = (annotation: ImageAnnotation) => {
    setSelectedAnnotation(annotation);
  };

  const handleAnnotationDelete = (annotationId: string) => {
    setAnnotations(prev => prev.filter(a => a.id !== annotationId));
    onAnnotationDelete?.(annotationId);
    setSelectedAnnotation(null);
  };

  const handleAnnotationUpdate = (updatedAnnotation: ImageAnnotation) => {
    setAnnotations(prev => 
      prev.map(a => a.id === updatedAnnotation.id ? updatedAnnotation : a)
    );
    onAnnotationUpdate?.(updatedAnnotation);
  };

  const handleDownload = () => {
    onImageDownload?.(imageUrl);
  };

  const handleShare = () => {
    onImageShare?.(imageUrl);
  };

  const renderAnnotation = (annotation: ImageAnnotation) => {
    const baseStyle = {
      position: 'absolute' as const,
      left: `${annotation.x}%`,
      top: `${annotation.y}%`,
      color: annotation.color,
      zIndex: 10
    };

    switch (annotation.type) {
      case 'text':
        return (
          <div
            key={annotation.id}
            style={baseStyle}
            className="cursor-pointer"
            onClick={() => handleAnnotationClick(annotation)}
          >
            <div className="bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-lg text-sm">
              {annotation.text}
            </div>
          </div>
        );
      
      case 'arrow':
        return (
          <div
            key={annotation.id}
            style={baseStyle}
            className="cursor-pointer"
            onClick={() => handleAnnotationClick(annotation)}
          >
            <ArrowRight className="h-6 w-6" style={{ color: annotation.color }} />
          </div>
        );
      
      case 'highlight':
        return (
          <div
            key={annotation.id}
            style={{
              ...baseStyle,
              width: `${annotation.width}%`,
              height: `${annotation.height}%`,
              backgroundColor: annotation.color,
              opacity: 0.3
            }}
            className="cursor-pointer"
            onClick={() => handleAnnotationClick(annotation)}
          />
        );
      
      case 'circle':
        return (
          <div
            key={annotation.id}
            style={{
              ...baseStyle,
              width: `${annotation.width}%`,
              height: `${annotation.width}%`,
              border: `2px solid ${annotation.color}`,
              borderRadius: '50%'
            }}
            className="cursor-pointer"
            onClick={() => handleAnnotationClick(annotation)}
          />
        );
      
      case 'rectangle':
        return (
          <div
            key={annotation.id}
            style={{
              ...baseStyle,
              width: `${annotation.width}%`,
              height: `${annotation.height}%`,
              border: `2px solid ${annotation.color}`
            }}
            className="cursor-pointer"
            onClick={() => handleAnnotationClick(annotation)}
          />
        );
      
      default:
        return null;
    }
  };

  if (imageError) {
    return (
      <div className="flex items-center justify-center p-8 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <div className="text-center">
          <Image className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Failed to load image
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            The image could not be loaded. Please check the URL and try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Main Image Container */}
      <div
        ref={containerRef}
        className={`relative bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden ${
          isFullscreen ? 'fixed inset-0 z-50' : ''
        }`}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
      >
        {/* Image */}
        <img
          ref={imageRef}
          src={imageUrl}
          alt={imageAlt}
          className={`w-full h-auto transition-all duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{
            transform: `scale(${zoom}) rotate(${rotation}deg)`,
            transformOrigin: 'center center'
          }}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />

        {/* Loading State */}
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Annotations */}
        {annotations.map(renderAnnotation)}

        {/* Controls Overlay */}
        {showControls && (
          <div className="absolute top-4 right-4 flex flex-col gap-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 flex flex-col gap-1">
              <button
                onClick={handleZoomIn}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Zoom In"
              >
                <ZoomIn className="h-4 w-4" />
              </button>
              <button
                onClick={handleZoomOut}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Zoom Out"
              >
                <ZoomOut className="h-4 w-4" />
              </button>
              <button
                onClick={handleResetZoom}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Reset Zoom"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button
                onClick={handleRotate}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Rotate Clockwise"
              >
                <RotateCw className="h-4 w-4" />
              </button>
              <button
                onClick={handleRotateCounterClockwise}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title="Rotate Counter-Clockwise"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
              <button
                onClick={handleFullscreen}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </button>
            </div>
          </div>
        )}

        {/* Annotation Tools */}
        {isAnnotating && (
          <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
            <div className="flex items-center gap-4">
              <div className="flex gap-2">
                <button
                  onClick={() => setAnnotationType('text')}
                  className={`p-2 rounded ${annotationType === 'text' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  title="Text Annotation"
                >
                  <Type className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setAnnotationType('arrow')}
                  className={`p-2 rounded ${annotationType === 'arrow' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  title="Arrow Annotation"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setAnnotationType('highlight')}
                  className={`p-2 rounded ${annotationType === 'highlight' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  title="Highlight Annotation"
                >
                  <Square className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setAnnotationType('circle')}
                  className={`p-2 rounded ${annotationType === 'circle' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  title="Circle Annotation"
                >
                  <CircleIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setAnnotationType('rectangle')}
                  className={`p-2 rounded ${annotationType === 'rectangle' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                  title="Rectangle Annotation"
                >
                  <SquareIcon className="h-4 w-4" />
                </button>
              </div>
              
              <div className="flex gap-1">
                {colors.map(color => (
                  <button
                    key={color}
                    onClick={() => setAnnotationColor(color)}
                    className={`w-6 h-6 rounded-full border-2 ${
                      annotationColor === color ? 'border-gray-900 dark:border-white' : 'border-gray-300 dark:border-gray-600'
                    }`}
                    style={{ backgroundColor: color }}
                    title={`Select ${color}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="absolute bottom-4 right-4 flex gap-2">
          <button
            onClick={() => setIsAnnotating(!isAnnotating)}
            className={`px-4 py-2 rounded-lg text-white ${
              isAnnotating ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {isAnnotating ? 'Stop Annotating' : 'Annotate'}
          </button>
          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            <Download className="h-4 w-4 inline mr-1" />
            Download
          </button>
          <button
            onClick={handleShare}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            <Share2 className="h-4 w-4 inline mr-1" />
            Share
          </button>
        </div>
      </div>

      {/* Annotation Panel */}
      {annotations.length > 0 && (
        <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Annotations ({annotations.length})
          </h3>
          <div className="space-y-2">
            {annotations.map(annotation => (
              <div
                key={annotation.id}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedAnnotation?.id === annotation.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => handleAnnotationClick(annotation)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: annotation.color }}
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {annotation.type.charAt(0).toUpperCase() + annotation.type.slice(1)}
                    </span>
                    {annotation.text && (
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        "{annotation.text}"
                      </span>
                    )}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAnnotationDelete(annotation.id);
                    }}
                    className="p-1 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Annotation Details */}
      {selectedAnnotation && (
        <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Annotation Details
          </h3>
          <div className="space-y-2">
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Type:</span>
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                {selectedAnnotation.type}
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Position:</span>
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                {Math.round(selectedAnnotation.x)}%, {Math.round(selectedAnnotation.y)}%
              </span>
            </div>
            {selectedAnnotation.text && (
              <div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Text:</span>
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                  {selectedAnnotation.text}
                </span>
              </div>
            )}
            <div>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Created:</span>
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                {selectedAnnotation.createdAt.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageEnhancement;
